/**
 * ListeningTimeRing — the 168px translucent ring on the home hero.
 *
 * iOS source: WeeklyCalendar.tsx:32-67. A 168px circle, 6px border at
 * rgba(255,255,255,0.35), translucent white fill, soft glow. Center shows the
 * big bold white time label + "Listening Time" caption.
 *
 * Uses the COMPACT formatter ("0s" / "5m 12s" / "1h 20m") — NOT the verbose
 * "0 minutes" formatter the old LearnPage used.
 */
import { FC } from "react";
import { colors } from "@/ui";

export interface ListeningTimeRingProps {
  /** Listening time in seconds. */
  seconds: number;
}

/** iOS compact format (WeeklyCalendar.tsx:36-41). */
function formatCompactListening(totalSeconds: number): string {
  const s = Math.max(0, Math.floor(totalSeconds));
  if (s < 60) return `${s}s`;
  if (s < 3600) {
    const m = Math.floor(s / 60);
    const rem = s % 60;
    return rem > 0 ? `${m}m ${rem}s` : `${m}m`;
  }
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return `${h}h ${m}m`;
}

const RING_SIZE = 168;

const ListeningTimeRing: FC<ListeningTimeRingProps> = ({ seconds }) => {
  return (
    <div
      style={{
        width: RING_SIZE,
        height: RING_SIZE,
        maxWidth: "70vw",
        aspectRatio: "1 / 1",
        borderRadius: "50%",
        border: `6px solid ${colors.whiteBold}`,
        background: "rgba(255,255,255,0.12)",
        boxShadow: "0 0 24px rgba(255,255,255,0.25)",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden",
        margin: "0 auto",
      }}
    >
      <span
        style={{
          // Fixed size: the ring is a fixed 168px, so the label must not scale
          // with the viewport (8vw hit 40px on desktop and overflowed the circle).
          fontSize: 32,
          fontWeight: 800,
          color: colors.white,
          lineHeight: 1.1,
          textAlign: "center",
        }}
      >
        {formatCompactListening(seconds)}
      </span>
      <span
        style={{
          fontSize: "var(--base-font-size-small)",
          fontWeight: 600,
          color: colors.white,
          opacity: 0.95,
          marginTop: 4,
        }}
      >
        Listening Time
      </span>
    </div>
  );
};

export default ListeningTimeRing;
